import { AntSelectPropsGeneric, AntSelectValue } from 'controls/react/ant/ant-select/types';
import { BaseOptionType } from '@rc-component/select/lib/Select';
import React, { useCallback } from 'react';
import {
	DragDropContext,
	Draggable,
	DraggableProvided,
	DraggableStateSnapshot,
	Droppable,
	DroppableProvided,
	DropResult,
} from '@hello-pangea/dnd';
import { CssVariables } from 'styles/cssVariables';
import { CustomTagProps } from '@rc-component/select/lib/BaseSelect';
import { SelectValue } from 'antd/lib/select';
import { LabeledValue } from 'antd/es/select';
import { getItemStyle } from 'controls/react/ant/ant-select/utils/getItemStyle';
import { HolderOutlined } from '@ant-design/icons';
import { reorder } from 'controls/react/ant/ant-select/utils/reorder';
import { AntTag } from 'controls/react/ant/ant-select/antSelect';

const hiddenStyles = { display: 'none' };

export function useReorderableMultiselect<VT extends AntSelectValue, OptionType extends BaseOptionType>(
	props: AntSelectPropsGeneric<VT, OptionType>,
) {
	const { tagRender, reorderable, ...rest } = props;
	const featureEnabled = (props.mode == 'multiple' || props.mode == 'tags') && reorderable == true;

	const value = Array.isArray(props.value) ? props.value : ([] as VT[]);

	const renderClone = useCallback(
		(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: { draggableId: string }) => {
			const style = {
				...provided.draggableProps.style,
				zIndex: CssVariables.zIndexControlPopover,
			};

			const label = rubric.draggableId;

			return (
				<div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={style}>
					<AntTag>{label}</AntTag>
				</div>
			);
		},
		[props.options],
	);

	const customTagRender = React.useCallback(
		(tagProps: CustomTagProps) => {
			const className = tagProps.closable ? 'ant-tag__closable' : null;
			const key = (tagProps.value as string)?.toString() ?? (tagProps.label as string);

			return (
				<Draggable
					key={key}
					draggableId={key}
					index={value.findIndex((item: SelectValue) =>
						props.labelInValue ? (item as LabeledValue).value === props.value : item === tagProps.value,
					)}
				>
					{(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
						<div
							ref={provided.innerRef}
							{...provided.draggableProps}
							{...provided.dragHandleProps}
							style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
							onMouseDown={(e) => {
								e.stopPropagation();
							}}
						>
							{tagRender && tagRender(tagProps)}
							{!tagRender && (
								<AntTag className={className} {...tagProps}>
									<HolderOutlined />
									{tagProps.label}
								</AntTag>
							)}
						</div>
					)}
				</Draggable>
			);
		},
		[value],
	);

	const wrapper = React.useCallback(
		(children: React.ReactNode) => {
			const onDragEnd = (result: DropResult) => {
				const {
					destination,
					source: { index: dragIndex },
				} = result;

				if (destination) {
					const items = reorder(props.value, dragIndex, destination.index);

					props.onChange?.(items, null);
				}
			};

			return (
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="list" direction="horizontal" renderClone={renderClone}>
						{(provided: DroppableProvided) => (
							<div className={'ant-select-droppable-wrapper'} {...provided.droppableProps} ref={provided.innerRef}>
								{/*the Droppable component fires an warning if {provided.placeholder} is not used but we dont need it*/}
								<div style={hiddenStyles}>{provided.placeholder}</div>
								{props.wrapper(children)}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			);
		},
		[props.value, props.onChange],
	);

	return {
		...rest,
		tagRender: featureEnabled ? customTagRender : tagRender,
		wrapper: featureEnabled ? wrapper : props.wrapper,
	};
}
